import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const peso = (n) =>
  '₱' + Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qty = (n) => (Number.isInteger(Number(n)) ? String(Number(n)) : Number(n).toFixed(2));
const SERVICE = { dine_in: 'Dine-in', take_out: 'Take-out', delivery: 'Delivery' };

/**
 * Customer-facing screen. Public on purpose: the unguessable token in the address is the credential,
 * so it opens on a second monitor or a tablet without anyone signing in. Read-only; it only shows what
 * the cashier's POS pushes (items, amounts and totals, never customer details).
 */
export default function CustomerDisplayPage() {
  const { token } = useParams();
  const [snap, setSnap] = useState(null);
  const [online, setOnline] = useState(true);
  const lastGood = useRef(0);

  useEffect(() => {
    document.title = 'Big Mummy’s | Your order';
    let stop = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API_URL}/api/display/${encodeURIComponent(token)}`, { cache: 'no-store' });
        if (!r.ok) throw new Error(String(r.status));
        const d = await r.json();
        if (!stop) {
          setSnap(d.snapshot);
          setOnline(true);
          lastGood.current = Date.now();
        }
      } catch {
        if (!stop && Date.now() - lastGood.current > 8000) setOnline(false);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => { stop = true; clearInterval(id); };
  }, [token]);

  const hasOrder = snap && snap.status === 'order' && snap.items && snap.items.length > 0;
  const paid = snap && snap.status === 'paid';

  return (
    <div className="min-h-screen flex flex-col bg-[#FFF6DC] text-[#3a0c10]" data-testid="customer-display">
      <header className="flex items-center justify-between px-6 md:px-10 py-4 border-b border-[#ecd9a8] bg-[#fffdf6]">
        <img src="/bigmummy-logo.png" alt="Big Mummy’s" className="h-12 md:h-16 w-auto" />
        <div className="text-right">
          <div className="font-heading text-lg md:text-2xl font-semibold">Your order</div>
          {snap && snap.service_type && SERVICE[snap.service_type] && (
            <div className="text-sm md:text-base text-[#7a5a4a]">{SERVICE[snap.service_type]}</div>
          )}
        </div>
      </header>

      {!online && (
        <div className="bg-amber-100 text-amber-900 text-center text-sm py-1.5" data-testid="display-offline">
          Reconnecting…
        </div>
      )}

      {hasOrder ? (
        <main className="flex-1 flex flex-col max-w-4xl w-full mx-auto px-4 md:px-8 py-4 md:py-6 min-h-0">
          <ul className="flex-1 overflow-y-auto divide-y divide-[#ecd9a8]" data-testid="display-items">
            {snap.items.map((it, i) => (
              <li key={i} className="flex items-baseline justify-between gap-4 py-3 md:py-4">
                <div className="min-w-0">
                  <span className="text-xl md:text-3xl font-medium leading-tight">{it.name}</span>
                  <span className="ml-3 text-lg md:text-2xl text-[#7a5a4a]">× {qty(it.quantity)}</span>
                  {it.discount > 0 && (
                    <div className="text-sm md:text-lg text-emerald-700">Discount −{peso(it.discount)}</div>
                  )}
                </div>
                <div className="text-xl md:text-3xl font-semibold tabular-nums whitespace-nowrap">{peso(it.line_total)}</div>
              </li>
            ))}
          </ul>

          <section className="mt-4 rounded-2xl bg-[#fffdf6] border border-[#ecd9a8] p-4 md:p-6" data-testid="display-totals">
            <Row label="Subtotal" value={peso(snap.subtotal)} />
            {snap.discount > 0 && <Row label="Discount" value={`−${peso(snap.discount)}`} tone="text-emerald-700" />}
            {snap.service_charge > 0 && <Row label="Service charge" value={peso(snap.service_charge)} />}
            <Row label="VAT (included)" value={peso(snap.vat)} muted />
            <div className="flex items-baseline justify-between mt-3 pt-3 border-t border-[#ecd9a8]">
              <span className="font-heading text-2xl md:text-4xl font-semibold">Total</span>
              <span className="text-4xl md:text-7xl font-bold text-[#C8161D] tabular-nums" data-testid="display-total">
                {peso(snap.total)}
              </span>
            </div>
          </section>
        </main>
      ) : paid ? (
        <main className="flex-1 flex flex-col items-center justify-center text-center px-6" data-testid="display-thanks">
          <div className="text-5xl md:text-8xl font-heading font-semibold">Thank you!</div>
          <div className="mt-4 text-xl md:text-3xl text-[#7a5a4a]">Paid {peso(snap.total)}</div>
          <div className="mt-8 text-lg md:text-2xl">Enjoy your meal! 🍛</div>
        </main>
      ) : (
        <main
          className="flex-1 relative flex items-end bg-cover bg-center"
          style={{ backgroundImage: "url('/bigmummy-facade.jpg')" }}
          data-testid="display-idle"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-[#2a0a0d]/85 via-[#2a0a0d]/30 to-transparent" />
          <div className="relative p-8 md:p-14 text-[#FFF6DC]">
            <div className="text-3xl md:text-6xl font-heading font-semibold">Welcome to Big Mummy’s</div>
            <div className="mt-2 text-lg md:text-3xl opacity-90">Affordable and authentic Indian cuisine</div>
            <div className="mt-3 text-sm md:text-lg opacity-75">Indian · Arabic cuisine · 75 Pacita Ave., Pacita Complex, San Pedro, Laguna</div>
          </div>
        </main>
      )}
    </div>
  );
}

function Row({ label, value, muted, tone }) {
  return (
    <div className={`flex items-baseline justify-between py-0.5 text-lg md:text-2xl ${muted ? 'text-[#7a5a4a]' : ''}`}>
      <span>{label}</span>
      <span className={`tabular-nums ${tone || ''}`}>{value}</span>
    </div>
  );
}
